import { TenantSlug } from "@repo/config/sharedConfig";
import { getDemoFeatures } from "@repo/config/tenants";
import { EHRSystem, JobType } from "@repo/db/client";
import { isValidDemoPhoneInput } from "@repo/services/models/demoPhoneFormat";
import {
	Button,
	Flex,
	Spacer,
	Text,
	TextField,
	WithDialog,
	useTheme,
} from "@repo/ui";
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { toast } from "sonner";
import { useApiClient } from "../contexts/ApiContext";
import { useLoggedInUser } from "../contexts/AuthContext";
import { useDatabase } from "../db/DatabaseProvider";
import { absoluteRoute, Routes } from "../routing";
import { getTenantSlug } from "../tenant";

type DemoPhones = {
	patientPhone: string;
	specialistPhone1: string;
	specialistPhone2: string;
	specialistFax: string;
};

const demoFeatures = getDemoFeatures(TenantSlug.Demo);
const DEMO_RESET_VETO_WINDOW_MS = 10_000;
const PRESENCE_ZERO_POLLS_BEFORE_CLEAR = 3;

type PendingKind = "reset" | "saveNumbers";

type PendingAction = {
	kind: PendingKind;
	remainingMs: number;
	receivedAt: number;
	requestedByClientId: string;
	vetoed: boolean;
};

const ACTION_COPY: Record<
	PendingKind,
	{
		requestedTitle: string;
		waitingTitle: string;
		cancelledTitle: string;
		vetoLabel: string;
		forceLabel: string;
		busyForceLabel: string;
		waitingBody: (secondsLeft: number) => string;
		vetoBody: (secondsLeft: number) => string;
		cancelledBody: string;
		vetoToast: string;
		vetoError: string;
		cancelledPrefix: string;
	}
> = {
	reset: {
		requestedTitle: "Reset Requested",
		waitingTitle: "Waiting to Reset...",
		cancelledTitle: "Reset Cancelled",
		vetoLabel: "Cancel Reset",
		forceLabel: "Force Reset",
		busyForceLabel: "Resetting…",
		waitingBody: (secondsLeft) =>
			`Waiting ${secondsLeft}s to give other presenters a chance to veto.`,
		vetoBody: (secondsLeft) =>
			`Another session is requesting to reset the referrals. Veto their request within ${secondsLeft}s to keep presenting. Otherwise, the inbox will be cleared.`,
		cancelledBody:
			"The other presenter vetoed the reset. Force reset to reset the demo anyway.",
		vetoToast: "Reset Cancelled.",
		vetoError: "Could not cancel reset",
		cancelledPrefix: "Reset cancelled",
	},
	saveNumbers: {
		requestedTitle: "Update Requested",
		waitingTitle: "Waiting to Update...",
		cancelledTitle: "Update Cancelled",
		vetoLabel: "Cancel Update",
		forceLabel: "Force Update",
		busyForceLabel: "Saving…",
		waitingBody: (secondsLeft) =>
			`Waiting ${secondsLeft}s to give other presenters a chance to veto.`,
		vetoBody: (secondsLeft) =>
			`Another session is requesting to update the demo numbers. Veto their request within ${secondsLeft}s to keep presenting. Otherwise, the numbers will change.`,
		cancelledBody:
			"The other presenter vetoed the update. Force update to change the numbers anyway.",
		vetoToast: "Update Cancelled.",
		vetoError: "Could not cancel update",
		cancelledPrefix: "Update cancelled",
	},
};

function applyControlSettings(
	settings: DemoPhones & { athenaUrl: string | null },
	setPhones: (phones: DemoPhones) => void,
	setAthenaUrl: (url: string | null) => void,
) {
	setPhones({
		patientPhone: settings.patientPhone,
		specialistPhone1: settings.specialistPhone1,
		specialistPhone2: settings.specialistPhone2,
		specialistFax: settings.specialistFax,
	});
	setAthenaUrl(settings.athenaUrl);
}

export function DemoControlBar() {
	const user = useLoggedInUser();
	if (getTenantSlug() !== TenantSlug.Demo || !user) {
		return null;
	}

	return <DemoControlBarInner />;
}

function DemoControlBarInner() {
	const api = useApiClient();
	const db = useDatabase();
	const [phones, setPhones] = useState<DemoPhones>({
		patientPhone: "",
		specialistPhone1: "",
		specialistPhone2: "",
		specialistFax: "",
	});
	const [athenaUrl, setAthenaUrl] = useState<string | null>(null);
	const [busy, setBusy] = useState<
		"save" | "reset" | "pull" | "check" | "veto" | null
	>(null);
	const [otherLiveSessionCount, setOtherLiveSessionCount] = useState(0);
	const [pullConfirmOpen, setPullConfirmOpen] = useState(false);
	const [setNumbersOpen, setSetNumbersOpen] = useState(false);
	const [pendingAction, setPendingAction] = useState<PendingAction | null>(
		null,
	);
	const [actionWaitOpen, setActionWaitOpen] = useState(false);
	const [forcingAction, setForcingAction] = useState(false);
	const pendingSavePhonesRef = useRef<DemoPhones | null>(null);
	const seenLastResetAtRef = useRef<string | null | undefined>(undefined);
	const seenLastSaveAtRef = useRef<string | null | undefined>(undefined);
	const lastOtherCountRef = useRef(0);
	const otherCountZeroPollsRef = useRef(0);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			try {
				const settings = await api.demo.getSettings();
				if (cancelled) return;
				applyControlSettings(settings, setPhones, setAthenaUrl);
			} catch (error) {
				console.error("Failed to load demo control settings", error);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [api]);

	const finishReset = useCallback(() => {
		toast.success("Demo Reset.");
		const taskListUrl = absoluteRoute(Routes.taskList);
		if (window.location.pathname === new URL(taskListUrl).pathname) {
			window.location.reload();
		} else {
			window.location.assign(taskListUrl);
		}
	}, []);

	const finishSave = useCallback(
		(settings: DemoPhones & { athenaUrl: string | null }) => {
			applyControlSettings(settings, setPhones, setAthenaUrl);
			toast.success("Numbers Saved.");
			setActionWaitOpen(false);
			setPendingAction(null);
			setForcingAction(false);
			setBusy(null);
		},
		[],
	);

	const refreshPresence = useCallback(async (): Promise<number> => {
		try {
			const presence = await api.demo.getPresence({
				clientId: db.clientId,
			});
			const liveCount = presence.otherLiveSessionCount;
			if (liveCount > 0) {
				lastOtherCountRef.current = liveCount;
				otherCountZeroPollsRef.current = 0;
				setOtherLiveSessionCount(liveCount);
			} else {
				otherCountZeroPollsRef.current += 1;
				if (
					otherCountZeroPollsRef.current >= PRESENCE_ZERO_POLLS_BEFORE_CLEAR
				) {
					lastOtherCountRef.current = 0;
					setOtherLiveSessionCount(0);
				}
			}
			const pending = presence.pendingReset
				? { kind: "reset" as const, ...presence.pendingReset }
				: presence.pendingSave
					? { kind: "saveNumbers" as const, ...presence.pendingSave }
					: null;
			if (pending) {
				setPendingAction({
					kind: pending.kind,
					remainingMs: pending.remainingMs,
					receivedAt: Date.now(),
					requestedByClientId: pending.requestedByClientId,
					vetoed: pending.vetoed,
				});
			} else if (!actionWaitOpen) {
				setPendingAction(null);
			}

			const lastReset = presence.lastReset;
			if (seenLastResetAtRef.current === undefined) {
				seenLastResetAtRef.current = lastReset?.at ?? null;
			} else if (lastReset && lastReset.at !== seenLastResetAtRef.current) {
				seenLastResetAtRef.current = lastReset.at;
				if (lastReset.byClientId !== db.clientId) {
					finishReset();
				}
			}

			const lastSave = presence.lastSave;
			if (seenLastSaveAtRef.current === undefined) {
				seenLastSaveAtRef.current = lastSave?.at ?? null;
			} else if (lastSave && lastSave.at !== seenLastSaveAtRef.current) {
				seenLastSaveAtRef.current = lastSave.at;
				if (lastSave.byClientId !== db.clientId) {
					try {
						const settings = await api.demo.getSettings();
						applyControlSettings(settings, setPhones, setAthenaUrl);
						toast.success("Numbers Updated.");
					} catch (error) {
						console.error("Failed to refresh demo numbers", error);
					}
				}
			}
			return lastOtherCountRef.current;
		} catch (error) {
			console.error("Failed to load demo presence", error);
			return lastOtherCountRef.current;
		}
	}, [actionWaitOpen, api, db.clientId, finishReset]);

	useEffect(() => {
		let cancelled = false;
		const poll = async () => {
			if (cancelled) return;
			await refreshPresence();
		};
		void poll();
		const intervalId = window.setInterval(() => {
			void poll();
		}, 1_000);
		return () => {
			cancelled = true;
			window.clearInterval(intervalId);
		};
	}, [refreshPresence]);

	const markLocalVeto = useCallback(
		(kind: PendingKind) => {
			setPendingAction((current) =>
				current
					? { ...current, remainingMs: 0, vetoed: true }
					: {
							kind,
							remainingMs: 0,
							receivedAt: Date.now(),
							requestedByClientId: db.clientId,
							vetoed: true,
						},
			);
			setBusy(null);
		},
		[db.clientId],
	);

	const handleSaveNumbers = useCallback(
		async (next: DemoPhones) => {
			if (busy) return;
			setBusy("save");
			setSetNumbersOpen(false);
			pendingSavePhonesRef.current = next;
			if (otherLiveSessionCount > 0) {
				setActionWaitOpen(true);
				setPendingAction({
					kind: "saveNumbers",
					remainingMs: DEMO_RESET_VETO_WINDOW_MS,
					receivedAt: Date.now(),
					requestedByClientId: db.clientId,
					vetoed: false,
				});
			}
			try {
				const settings = await api.demo.saveSettings({
					...next,
					clientId: db.clientId,
				});
				finishSave(settings);
			} catch (error) {
				console.error("Failed to save demo phones", error);
				if (
					error instanceof Error &&
					error.message.startsWith(ACTION_COPY.saveNumbers.cancelledPrefix)
				) {
					markLocalVeto("saveNumbers");
					return;
				}
				toast.error("Could not save demo numbers");
				setActionWaitOpen(false);
				setBusy(null);
			}
		},
		[api, busy, db.clientId, finishSave, markLocalVeto, otherLiveSessionCount],
	);

	const handleReset = useCallback(async () => {
		if (busy) return;
		if (
			!demoControlPhonesAreValid(phones, Boolean(demoFeatures?.sendRealFax))
		) {
			return;
		}
		setBusy("reset");
		if (otherLiveSessionCount > 0) {
			setActionWaitOpen(true);
			setPendingAction({
				kind: "reset",
				remainingMs: DEMO_RESET_VETO_WINDOW_MS,
				receivedAt: Date.now(),
				requestedByClientId: db.clientId,
				vetoed: false,
			});
		}
		try {
			await api.demo.reset({ clientId: db.clientId });
			finishReset();
		} catch (error) {
			console.error("Failed to reset demo", error);
			if (
				error instanceof Error &&
				error.message.startsWith(ACTION_COPY.reset.cancelledPrefix)
			) {
				markLocalVeto("reset");
				return;
			}
			toast.error(
				error instanceof Error ? error.message : "Could not reset demo",
			);
			setActionWaitOpen(false);
			setBusy(null);
		}
	}, [
		api,
		busy,
		db.clientId,
		finishReset,
		markLocalVeto,
		otherLiveSessionCount,
		phones,
	]);

	const handleForceAction = useCallback(async () => {
		if (forcingAction) return;
		const kind = pendingAction?.kind;
		if (!kind) return;
		setForcingAction(true);
		setBusy(kind === "reset" ? "reset" : "save");
		try {
			if (kind === "reset") {
				await api.demo.reset({ clientId: db.clientId, force: true });
				finishReset();
				return;
			}
			const settings = await api.demo.saveSettings({
				...(pendingSavePhonesRef.current ?? phones),
				clientId: db.clientId,
				force: true,
			});
			finishSave(settings);
		} catch (error) {
			console.error("Failed to force demo action", error);
			toast.error(
				error instanceof Error
					? error.message
					: kind === "reset"
						? "Could not reset demo"
						: "Could not save demo numbers",
			);
			setForcingAction(false);
			setBusy(null);
		}
	}, [
		api,
		db.clientId,
		finishReset,
		finishSave,
		forcingAction,
		pendingAction?.kind,
		phones,
	]);

	const handleVetoAction = useCallback(async () => {
		if (busy) return;
		const kind = pendingAction?.kind;
		if (!kind) return;
		setBusy("veto");
		const copy = ACTION_COPY[kind];
		try {
			const result =
				kind === "reset"
					? await api.demo.vetoReset({ clientId: db.clientId })
					: await api.demo.vetoSave({ clientId: db.clientId });
			if (result.vetoed) {
				setPendingAction(null);
				toast.success(copy.vetoToast);
			}
		} catch (error) {
			console.error("Failed to veto demo action", error);
			toast.error(copy.vetoError);
		} finally {
			setBusy(null);
		}
	}, [api, busy, db.clientId, pendingAction?.kind]);

	const runPull = useCallback(async () => {
		try {
			if (demoFeatures?.pullRealReferrals) {
				const response = await api.tools.executeBrowserAgentJob({
					browserAgentJobTask: JobType.PullOpenReferrals,
					parameters: {
						vendorName: EHRSystem.Athena,
					},
				});
				toast.success(`Pull started (${response.jobId.slice(0, 8)}…)`);
			} else {
				await api.demo.pullReferrals();
				toast.success("Referrals pulled.");
				// Tandem coalesces pulls within 150ms. Wait so we do not join a
				// pull that started before the server bumped task updatedAt.
				await new Promise((resolve) => {
					window.setTimeout(resolve, 200);
				});
				await db.pullFromRemote();
			}
		} catch (error) {
			console.error("Failed to pull referrals", error);
			toast.error("Could not pull referrals");
		} finally {
			setBusy(null);
		}
	}, [api, db]);

	const handlePull = useCallback(async () => {
		if (busy) return;
		if (
			!demoControlPhonesAreValid(phones, Boolean(demoFeatures?.sendRealFax))
		) {
			return;
		}
		setBusy("pull");
		await runPull();
	}, [busy, phones, runPull]);

	const handlePullClick = useCallback(async () => {
		if (busy) return;
		if (
			!demoControlPhonesAreValid(phones, Boolean(demoFeatures?.sendRealFax))
		) {
			return;
		}
		setBusy("check");
		const others = await refreshPresence();
		if (others > 0) {
			setBusy(null);
			setPullConfirmOpen(true);
			return;
		}
		setBusy("pull");
		await runPull();
	}, [busy, phones, refreshPresence, runPull]);

	const showFax = Boolean(demoFeatures?.sendRealFax);
	const numbersReady = demoControlPhonesAreValid(phones, showFax);
	const showOpenAthena = Boolean(demoFeatures?.pullRealReferrals && athenaUrl);

	return (
		<>
			<Flex
				align="center"
				gap="md"
				padding="sm"
				background="sand2"
				borderBottomWidth="md"
				borderColor="sandA3"
				style={{ flexShrink: 0, width: "100%" }}
			>
				<Text size="sm">
					{numbersReady
						? "Use these buttons to set up the demo:"
						: "Set valid numbers before pulling referrals:"}
				</Text>
				<Spacer grow />
				<WithDialog
					title="Set Numbers"
					isOpen={setNumbersOpen}
					onOpenChange={setSetNumbersOpen}
					dialog={
						<SetNumbersDialogBody
							phones={phones}
							busy={busy === "save"}
							showFax={showFax}
							otherLiveSessionCount={otherLiveSessionCount}
							onOpen={refreshPresence}
							onSave={(next) => {
								void handleSaveNumbers(next);
							}}
						/>
					}
				>
					<Button size="sm" variant="outline">
						Set Numbers
					</Button>
				</WithDialog>
				<Button
					size="sm"
					variant="outline"
					isDisabled={!numbersReady}
					onPress={() => {
						void handlePullClick();
					}}
				>
					{busy === "pull" ? "Pulling…" : "Pull Referrals"}
				</Button>
				{showOpenAthena ? (
					<Button
						size="sm"
						variant="outline"
						onPress={() => {
							window.open(athenaUrl!, "_blank", "noopener,noreferrer");
						}}
					>
						Open Athena
					</Button>
				) : null}
				<WithDialog
					title="Reset Demo"
					dialog={
						<ConfirmActionDialogBody
							busy={busy === "reset"}
							busyLabel="Resetting…"
							confirmLabel="Reset Demo"
							otherLiveSessionCount={otherLiveSessionCount}
							onOpen={refreshPresence}
							onConfirm={() => {
								void handleReset();
							}}
						>
							Are you sure you want to reset the demo? This will clear all
							referrals from the inbox.
						</ConfirmActionDialogBody>
					}
				>
					<Button size="sm" variant="outline" isDisabled={!numbersReady}>
						{busy === "reset" ? "Resetting…" : "Reset Demo"}
					</Button>
				</WithDialog>
				{otherLiveSessionCount > 0 ? (
					<OtherPresentersBadge count={otherLiveSessionCount} />
				) : null}
			</Flex>
			<WithDialog
				title="Pull Referrals"
				isOpen={pullConfirmOpen}
				onOpenChange={setPullConfirmOpen}
				dialog={
					<ConfirmActionDialogBody
						busy={busy === "pull"}
						busyLabel="Pulling…"
						confirmLabel="Pull Referrals"
						otherLiveSessionCount={otherLiveSessionCount}
						onOpen={refreshPresence}
						onConfirm={() => {
							void handlePull();
						}}
					/>
				}
			/>
			<WithDialog
				title={
					pendingAction
						? ACTION_COPY[pendingAction.kind].requestedTitle
						: "Reset Requested"
				}
				isOpen={Boolean(
					pendingAction &&
						!pendingAction.vetoed &&
						pendingAction.requestedByClientId !== db.clientId,
				)}
				isDismissable={false}
				dialog={
					<ActionVetoDialogBody
						copy={ACTION_COPY[pendingAction?.kind ?? "reset"]}
						remainingMs={pendingAction?.remainingMs ?? 0}
						receivedAt={pendingAction?.receivedAt ?? Date.now()}
						busy={busy === "veto"}
						onVeto={() => {
							void handleVetoAction();
						}}
					/>
				}
			/>
			<WithDialog
				title={
					pendingAction
						? pendingAction.vetoed
							? ACTION_COPY[pendingAction.kind].cancelledTitle
							: ACTION_COPY[pendingAction.kind].waitingTitle
						: "Waiting to Reset..."
				}
				isOpen={actionWaitOpen}
				isDismissable={false}
				dialog={
					<ActionWaitDialogBody
						copy={ACTION_COPY[pendingAction?.kind ?? "reset"]}
						remainingMs={pendingAction?.remainingMs ?? 0}
						receivedAt={pendingAction?.receivedAt ?? Date.now()}
						vetoed={Boolean(pendingAction?.vetoed)}
						busy={forcingAction}
						onForce={() => {
							void handleForceAction();
						}}
						onReturnToDemo={() => {
							setActionWaitOpen(false);
							setPendingAction(null);
							setForcingAction(false);
							setBusy(null);
						}}
					/>
				}
			/>
		</>
	);
}

function useCountdownSeconds(remainingMs: number, receivedAt: number): number {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const intervalId = window.setInterval(() => {
			setNow(Date.now());
		}, 250);
		return () => {
			window.clearInterval(intervalId);
		};
	}, []);

	return Math.max(0, Math.ceil((remainingMs - (now - receivedAt)) / 1000));
}

function ActionWaitDialogBody({
	copy,
	remainingMs,
	receivedAt,
	vetoed,
	busy,
	onForce,
	onReturnToDemo,
}: {
	copy: (typeof ACTION_COPY)[PendingKind];
	remainingMs: number;
	receivedAt: number;
	vetoed: boolean;
	busy: boolean;
	onForce: () => void;
	onReturnToDemo: () => void;
}) {
	const secondsLeft = useCountdownSeconds(remainingMs, receivedAt);

	return (
		<Flex column gap="md">
			<Text size="sm">
				{vetoed ? copy.cancelledBody : copy.waitingBody(secondsLeft)}
			</Text>
			{vetoed ? (
				<Flex gap="sm" justify="flex-end">
					<Button size="sm" variant="outline" onPress={() => onForce()}>
						{busy ? copy.busyForceLabel : copy.forceLabel}
					</Button>
					<Button size="sm" onPress={() => onReturnToDemo()}>
						Return to Demo
					</Button>
				</Flex>
			) : null}
		</Flex>
	);
}

function ActionVetoDialogBody({
	copy,
	remainingMs,
	receivedAt,
	busy,
	onVeto,
}: {
	copy: (typeof ACTION_COPY)[PendingKind];
	remainingMs: number;
	receivedAt: number;
	busy: boolean;
	onVeto: () => void;
}) {
	const secondsLeft = useCountdownSeconds(remainingMs, receivedAt);

	return (
		<Flex column gap="md">
			<Text size="sm">{copy.vetoBody(secondsLeft)}</Text>
			<Flex gap="sm" justify="flex-end">
				<Button
					size="sm"
					onPress={() => {
						if (!busy) onVeto();
					}}
				>
					{busy ? "Cancelling…" : copy.vetoLabel}
				</Button>
			</Flex>
		</Flex>
	);
}

function OtherPresentersBadge({ count }: { count: number }) {
	return (
		<Flex
			alignSelf="stretch"
			align="center"
			justify="center"
			padding="xs"
			borderRadius="md"
			background="amber3"
			borderWidth="md"
			borderColor="amber7"
			overflow="hidden"
			role="status"
		>
			<Text size="sm" weight="medium" color="amber11" capsize>
				{count} Other Presenter{count === 1 ? "" : "s"}
			</Text>
		</Flex>
	);
}

function OtherSessionWarning({
	otherLiveSessionCount,
}: {
	otherLiveSessionCount: number;
}) {
	if (otherLiveSessionCount <= 0) return null;
	return (
		<Text size="sm" color="amber11">
			Another demo session is currently live. Any actions you take will also
			affect their demo.
		</Text>
	);
}

// Presence can briefly report 0 while both tabs are live. Keep the last
// positive count for the life of the confirm so the warning does not flash.
function useLatchedOtherSessionCount(count: number): number {
	const [latched, setLatched] = useState(count);
	useEffect(() => {
		if (count > 0) {
			setLatched(count);
		}
	}, [count]);
	return Math.max(latched, count);
}

function ConfirmActionDialogBody({
	busy,
	busyLabel,
	confirmLabel,
	otherLiveSessionCount,
	onOpen,
	onConfirm,
	children,
}: {
	busy: boolean;
	busyLabel: string;
	confirmLabel: string;
	otherLiveSessionCount: number;
	onOpen: () => void | Promise<unknown>;
	onConfirm: () => void;
	children?: ReactNode;
}) {
	const latchedOtherSessionCount = useLatchedOtherSessionCount(
		otherLiveSessionCount,
	);
	const onOpenRef = useRef(onOpen);
	onOpenRef.current = onOpen;
	useEffect(() => {
		void onOpenRef.current();
	}, []);

	return (
		<Flex column gap="md">
			{children ? <Text size="sm">{children}</Text> : null}
			<OtherSessionWarning otherLiveSessionCount={latchedOtherSessionCount} />
			<Flex gap="sm" justify="flex-end">
				<Button size="sm" variant="outline" internal_slot="close">
					Cancel
				</Button>
				<Button
					size="sm"
					internal_slot="close"
					onPress={() => {
						if (!busy) onConfirm();
					}}
				>
					{busy ? busyLabel : confirmLabel}
				</Button>
			</Flex>
		</Flex>
	);
}

function demoControlPhonesAreValid(
	phones: DemoPhones,
	showFax: boolean,
): boolean {
	return (
		isValidDemoPhoneInput(phones.patientPhone) &&
		isValidDemoPhoneInput(phones.specialistPhone1) &&
		isValidDemoPhoneInput(phones.specialistPhone2) &&
		(!showFax || isValidDemoPhoneInput(phones.specialistFax))
	);
}

function demoNumberFieldError(
	value: string,
	kind: "phone" | "fax",
): string | undefined {
	if (isValidDemoPhoneInput(value)) {
		return undefined;
	}
	const empty = value.replace(/\D/g, "").length === 0;
	if (kind === "fax") {
		return empty ? "Fax is required" : "Fax must be 10 digits";
	}
	return empty ? "Phone is required" : "Phone must be 10 digits";
}

function SetNumbersDialogBody({
	phones,
	busy,
	showFax,
	otherLiveSessionCount,
	onOpen,
	onSave,
}: {
	phones: DemoPhones;
	busy: boolean;
	showFax: boolean;
	otherLiveSessionCount: number;
	onOpen: () => void | Promise<unknown>;
	onSave: (next: DemoPhones) => void;
}) {
	const theme = useTheme();
	const compactFieldStyle = { gap: theme.spacing.xs };
	const [draft, setDraft] = useState(phones);
	const [errors, setErrors] = useState<
		Partial<Record<keyof DemoPhones, string>>
	>({});
	const latchedOtherSessionCount = useLatchedOtherSessionCount(
		otherLiveSessionCount,
	);
	const onOpenRef = useRef(onOpen);
	onOpenRef.current = onOpen;

	useEffect(() => {
		setDraft(phones);
		setErrors({});
	}, [phones]);

	useEffect(() => {
		void onOpenRef.current();
	}, []);

	const setField = (field: keyof DemoPhones, value: string) => {
		setDraft((current) => ({ ...current, [field]: value }));
		setErrors((current) =>
			current[field] ? { ...current, [field]: undefined } : current,
		);
	};

	const validate = (): boolean => {
		const nextErrors: Partial<Record<keyof DemoPhones, string>> = {};
		const phoneFields = [
			"patientPhone",
			"specialistPhone1",
			"specialistPhone2",
		] as const;
		for (const field of phoneFields) {
			const error = demoNumberFieldError(draft[field], "phone");
			if (error) {
				nextErrors[field] = error;
			}
		}
		if (showFax) {
			const error = demoNumberFieldError(draft.specialistFax, "fax");
			if (error) {
				nextErrors.specialistFax = error;
			}
		}
		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	return (
		<Flex column gap="md">
			<Text size="sm">
				These numbers are used for patient texts and specialist calls during the
				demo.
			</Text>
			<OtherSessionWarning otherLiveSessionCount={latchedOtherSessionCount} />
			<Flex column gap="xl">
				<TextField
					label="Patient Phone"
					placeholder="e.g., (555) 123-4567"
					style={compactFieldStyle}
					value={draft.patientPhone}
					error={errors.patientPhone}
					onChange={(event) => setField("patientPhone", event.target.value)}
				/>
				<TextField
					label="Specialist Phone #1"
					placeholder="e.g., (555) 123-4567"
					style={compactFieldStyle}
					value={draft.specialistPhone1}
					error={errors.specialistPhone1}
					onChange={(event) => setField("specialistPhone1", event.target.value)}
				/>
				<TextField
					label="Specialist Phone #2"
					placeholder="e.g., (555) 123-4567"
					style={compactFieldStyle}
					value={draft.specialistPhone2}
					error={errors.specialistPhone2}
					onChange={(event) => setField("specialistPhone2", event.target.value)}
				/>
				{showFax ? (
					<TextField
						label="Specialist Fax"
						placeholder="e.g., (555) 123-4567"
						style={compactFieldStyle}
						value={draft.specialistFax}
						error={errors.specialistFax}
						onChange={(event) => setField("specialistFax", event.target.value)}
					/>
				) : null}
			</Flex>
			<Flex gap="sm" justify="flex-end">
				<Button size="sm" variant="outline" internal_slot="close">
					Cancel
				</Button>
				<Button
					size="sm"
					onPress={() => {
						if (busy) return;
						if (!validate()) return;
						onSave(draft);
					}}
				>
					{busy ? "Saving…" : "Save Numbers"}
				</Button>
			</Flex>
		</Flex>
	);
}
